// Count of records waiting for SuperAdmin review (events + artists + videos + organizers),
// for the floating corner menu badge. Fetched on load and on every Inertia page
// change, and pushed live over AdminChannel whenever a record enters or leaves
// the queue, so the badge moves without navigating. #1910
import { useCallback, useEffect, useState } from 'react';
import { router } from '@inertiajs/react';
import { useAdminChannel, type AdminChannelEvent } from './useAdminChannel';

const fetchPendingCount = async (): Promise<number | null> => {
  try {
    const response = await fetch('/api/v1/admin/pending_count', {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    const data = await response.json();
    return typeof data?.count === 'number' ? data.count : null;
  } catch {
    return null;
  }
};

export function usePendingCount(enabled: boolean): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;

    let active = true;
    const refresh = () => {
      fetchPendingCount().then((next) => {
        if (active && next != null) setCount(next);
      });
    };

    refresh();
    const stopListening = router.on('navigate', refresh);
    return () => {
      active = false;
      stopListening();
    };
  }, [enabled]);

  const handleAdminEvent = useCallback((data: AdminChannelEvent) => {
    if (data.event === 'pending_count_changed' && typeof data.count === 'number') {
      setCount(data.count);
    }
  }, []);
  useAdminChannel(handleAdminEvent, enabled);

  return enabled ? count : 0;
}
